import { StockCheck, StockCheckItem } from '../../app/services/api/generated';
import { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useNswagClient from '../../hooks/api/useNswagClient';
import { compare } from '../../utils';
import { Alert, Box, Button, Collapse, Grid, IconButton, Skeleton, Snackbar, Typography, useTheme, CircularProgress } from '@mui/material';
import StockCheckHeader from './components/StockCheckTop';
import StockCheckContent from './components/StockCheckContent';
import StockCheckApproveDialog from './components/StockCheckApproveDialog';
import useLogError from '../../hooks/useLogError';
import { useTranslation } from 'react-i18next';
import { ChevronUp, ChevronDown, ArrowLeft } from '../../assets';
import { UserContext } from './../../components/shared/useUser';
import PageTitle from '../../components/shared/PageTitle';
import PageArea from '../../components/shared/PageArea';
import { Pill } from './../../components/shared/Pill';
import { StockCheckType, StockCheckTypeLabels } from '../../enums/StockCheckType';
import useCurrencyCodeConfiguration from '../../hooks/configurations/useCurrencyCodeConfiguration';
import { ConfigurationName } from '../../enums/ConfigurationName';
import { ApproverType } from '../../enums/ApproverType';

export const StockCheckStatusMap = {
  1: 'Counting',
  2: 'Complete',
  3: 'Pending',
  4: 'Pending Approval',
};

export interface TempStockCheckItem extends StockCheckItem {
  isExpanded: boolean;
}

export interface TempCategoryItem {
  name: string;
  items: TempStockCheckItem[];
}

export interface TempCountZoneItem {
  name: string;
  rowNumber?: number;
  categories: TempCategoryItem[];
}

export interface TempStockCheck extends StockCheck {
    tempCountZoneItems?: TempCountZoneItem[];
    tempCountZoneAllItems?: TempCountZoneItem[];
    approverType?: number;
    approverRoleId?: number;
}

export interface GroupedStockCheckDetails {
  stockCheck?: TempStockCheck;
  lastPosDate?: string;
  hasPosData?: boolean;
  maxToleranceWarningValue?: number;
}

const StockCountReviewPage = () => {
  const { getStockCheckById, approveStockCheck } = useNswagClient();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation('stockCountReview');
  const { id } = useParams<{ id: string | undefined }>();
  const stockCheckId = id ? parseInt(id) : 0;
  const { logError } = useLogError();
  const [loading, setLoading] = useState<boolean>(false);
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const [groupedStockCount, setGroupedStockCount] = useState<GroupedStockCheckDetails>();
  const [isViewMode, setIsViewMode] = useState<boolean>(false);
  const [isTypeViewMode, setIsTypeViewMode] = useState<boolean>(false);
  const [showApproveDialog, setShowApproveDialog] = useState<boolean>(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string>('');
  const [showSubmitStatusMessage, setShowSubmitStatusMessage] = useState<boolean>(false);
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [itemsAboveMaxTolerance, setItemsAboveMaxTolerance] = useState<StockCheckItem[]>([]);
  const { hasPermissionTo, user } = useContext(UserContext);
  const [isBulkUpdating, setIsBulkUpdating] = useState<boolean>(false);
  const [bulkApprovalState, setBulkApprovalState] = useState<boolean | null>(null);
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  useEffect(() => {
    if (stockCheckId !== 0) {
      getStockCheck(stockCheckId);
    }
  }, [isSubmittedSuccessfully]);

  const getStockCheck = (id: number) => {
    setLoading(true);
    getStockCheckById(id).then((response) => {
      if (response.data) {
        const formattedStockCount = groupItemsByCategory(response.data);
        setGroupedStockCount(formattedStockCount);

        if (formattedStockCount?.stockCheck?.status === 2) {
          setIsCompleted(true);
        }
      }
    }).catch((error) => {
      logError(error);
    }).finally(() => {
      setLoading(false);
    });
  };

  const createLocationCategoryHierarchy = (stockCheck: StockCheck) => {
    const locations = stockCheck.locations ?? [];
    const items = stockCheck.items ?? [];

    if (!locations || locations.length === 0) {
      const categoriesArr = groupStockCheckItemsByCategory(items);
      return [{
        name: 'No Location',
        rowNumber: undefined,
        categories: categoriesArr,
      }];
    }

    const sortedLocations = [...locations].sort((a, b) => {
      if (a.rowNumber !== undefined && b.rowNumber !== undefined) {
        return a.rowNumber - b.rowNumber;
      }
      if (a.rowNumber !== undefined) return -1;
      if (b.rowNumber !== undefined) return 1;
      return (a.locationName || '').localeCompare(b.locationName || '');
    });

    const locationArr: TempCountZoneItem[] = sortedLocations.map(location => {
      const locationItems = items.filter(item => item.locationId === location.locationId);
      const categoriesArr = groupStockCheckItemsByCategory(locationItems);

      return {
        name: location.locationName ?? 'No Location',
        rowNumber: location.rowNumber,
        categories: categoriesArr,
      };
    });

    return locationArr;
  };

  const groupItemsByCategory = (stockCountData: GroupedStockCheckDetails) => {
    if ((stockCountData.stockCheck?.items?.length ?? 0) <= 0) {
      return;
    }

    if (stockCountData?.stockCheck) {
      stockCountData.stockCheck.tempCountZoneItems = [];
      stockCountData.stockCheck.tempCountZoneAllItems = [];

      const locationCategoryHierarchy = createLocationCategoryHierarchy(stockCountData.stockCheck);

      stockCountData.stockCheck.tempCountZoneItems = locationCategoryHierarchy;
      stockCountData.stockCheck.tempCountZoneAllItems = locationCategoryHierarchy;
    }

    return stockCountData;
  };

  const groupStockCheckItemsByCategory = (items: StockCheckItem[]): TempCategoryItem[] => {
    const categoryTypes: Record<string, TempCategoryItem> = {};

    for (const item of items) {
      const categoryName = item.category && item.category.trim() !== '' ? item.category : 'No Category';
      if (!categoryTypes[categoryName]) {
        categoryTypes[categoryName] = {
          name: categoryName,
          items: [],
        };
      }
      categoryTypes[categoryName].items.push({
        ...item,
        isExpanded: false,
      });
    }

    return sortItemsByRowNumberAndDescription(categoryTypes);
  };

  const sortItemsByRowNumberAndDescription = (categories: Record<string, TempCategoryItem>): TempCategoryItem[] => {
    return Object.values(categories).map(cat => ({
      ...cat,
      items: cat.items.sort((a, b) => {
        if (a.rowNumber !== undefined && b.rowNumber !== undefined) {
          return a.rowNumber - b.rowNumber;
        }
        if (a.rowNumber !== undefined) return -1;
        if (b.rowNumber !== undefined) return 1;
        return (a.description || '').localeCompare(b.description || '');
      }),
    })).sort((a, b) => a.name.localeCompare(b.name));
  };

  const updateAllItems = useCallback((checked: boolean) => {
    if (saveErrorMessage !== '') {
      setSaveErrorMessage('');
    }

    setBulkApprovalState(checked);
    setIsBulkUpdating(true);

    // Then start the background state update
    setTimeout(() => {
      setGroupedStockCount((prev) => {
        if (!prev?.stockCheck?.tempCountZoneItems) return prev;

        const updatedData = prev.stockCheck.tempCountZoneItems.map((tempCountZoneItem: TempCountZoneItem) => ({
          ...tempCountZoneItem,
          categories: tempCountZoneItem.categories.map((tempCategoryItem: TempCategoryItem) => ({
            ...tempCategoryItem,
            items: (tempCategoryItem.items ?? []).map((item: TempStockCheckItem) => ({
              ...item,
              isApproved: checked,
            })),
          })),
        }));

        return {
          ...prev,
          stockCheck: {
            ...prev.stockCheck,
            tempCountZoneItems: updatedData,
          },
        };
      });

      // Clear optimistic state after actual state is updated
      requestAnimationFrame(() => {
        setBulkApprovalState(null);
        setIsBulkUpdating(false);
      });
    }, 0);
  }, [saveErrorMessage]);

  const rejectAllApprovalItems = useCallback(() => {
    updateAllItems(false);
  }, [updateAllItems]);

  const approveAllApprovalItems = useCallback(() => {
    updateAllItems(true);
  }, [updateAllItems]);

  const hasUndefinedStockCheckItems = (): boolean => {
    if (!groupedStockCount?.stockCheck?.tempCountZoneItems) {
      return true;
    }

    // Count items that still have undefined/null isApproved
    let itemsWithUndefinedApproval = 0;

    for (const zone of groupedStockCount.stockCheck.tempCountZoneItems) {
      for (const category of zone.categories) {
        itemsWithUndefinedApproval += category.items.filter(
          (item) => item.isApproved === null || item.isApproved === undefined,
        ).length;
      }
    }

    // Return true if there are any items without approval decision
    return itemsWithUndefinedApproval > 0;
  };

  const onShowConfirmApprovalModal = () => {
    if (hasUndefinedStockCheckItems()) {
      setSaveErrorMessage(t('saveErrorMessage'));
      return;
    }

    const maxToleranceItems = groupedStockCount?.stockCheck?.tempCountZoneItems
      ? groupedStockCount.stockCheck.tempCountZoneItems.flatMap(zone => zone.categories.flatMap(category => category.items))
        .filter(item => item.isApproved && Math.abs(item.varianceValue ?? 0) > (groupedStockCount.maxToleranceWarningValue ?? 0))
        .map(item => ({ ...item, isApproved: undefined }))
      : [];

    setItemsAboveMaxTolerance(maxToleranceItems);

    setShowApproveDialog(true);
  };

  const getAllTempCategoryItems = (): StockCheckItem[] => {
    const updatedStockCheckItems: StockCheckItem[] = groupedStockCount?.stockCheck?.tempCountZoneItems
      ? groupedStockCount.stockCheck.tempCountZoneItems.flatMap(zone => zone.categories.flatMap(category => category.items))
      : [];

    const itemsMap = new Map<number | undefined, StockCheckItem>();

    updatedStockCheckItems.forEach(item => {
      itemsMap.set(item.id, { ...item });
    });

    const autoApprovedItems: StockCheckItem[] = groupedStockCount?.stockCheck?.items
      ? groupedStockCount?.stockCheck?.items
        .filter((item: StockCheckItem) => {
          const isAutoApproved = Number(item.quantityOnHand).toFixed(2) === Number(item.countQuantityTotal).toFixed(2);
          const notAlreadyProcessed = !itemsMap.has(item.id);
          return isAutoApproved && notAlreadyProcessed;
        })
        .map((item: StockCheckItem) => ({
          ...item,
          isApproved: true,
        }))
      : [];

    // Add auto-approved items to the map
    autoApprovedItems.forEach(item => {
      itemsMap.set(item.id, item);
    });

    return Array.from(itemsMap.values());
  };

  const submitApprovedStockCount = () => {
    if (groupedStockCount?.stockCheck?.tempCountZoneItems) {
      setSaveErrorMessage('');
      setIsApproving(true);

      const updatedStockCheckItems = getAllTempCategoryItems();

      approveStockCheck(stockCheckId, updatedStockCheckItems).then(() => {
        setIsSubmittedSuccessfully(true);
      }).catch((error) => {
        logError(error);
        setErrorMessage(error);
        setIsSubmittedSuccessfully(false);
      }).finally(() => {
        setIsApproving(false);
        setShowApproveDialog(false);
        setShowSubmitStatusMessage(true);
      });
    }
  };

  const handleItemStatusChange = useCallback((itemId: number | undefined, checked: boolean) => {
    if (saveErrorMessage !== '') {
      setSaveErrorMessage('');
    }

    setGroupedStockCount((prev) => {
      if (!prev?.stockCheck?.tempCountZoneItems) return prev;

      const updatedData: TempCountZoneItem[] = prev.stockCheck.tempCountZoneItems.map(zone => {
        const hasItem = zone.categories.some(cat =>
          cat.items.some(item => item.id === itemId),
        );

        if (!hasItem) return zone;

        const updatedCategories = zone.categories.map(category => {
          const hasItemInCategory = category.items.some(item => item.id === itemId);

          if (!hasItemInCategory) return category;

          const updatedItems = category.items.map((item: TempStockCheckItem) => {
            if (item.id === itemId && item.isApproved !== checked) {
              return {
                ...item,
                isApproved: checked,
              };
            }
            return item;
          });

          return {
            ...category,
            items: updatedItems,
          };
        });

        return {
          ...zone,
          categories: updatedCategories,
        };
      });

      return {
        ...prev,
        stockCheck: {
          ...prev.stockCheck,
          tempCountZoneItems: updatedData,
        },
      };
    });
  }, [saveErrorMessage]);

  const sortPartialStockCountItems = useCallback((zoneIndex: number, categoryIndex: number, fieldToSort: keyof TempStockCheckItem, sortDirection: 'asc' | 'desc') => {
    setGroupedStockCount((prev) => {
      if (!prev?.stockCheck?.tempCountZoneItems) return prev;

      const updatedData = prev.stockCheck.tempCountZoneItems.map((zone, zIndex) => ({
        ...zone,
        categories: zone.categories.map((category, cIndex) => ({
          ...category,
          items: zIndex === zoneIndex && cIndex === categoryIndex
            ? [...category.items].sort((a, b) => compare(a, b, fieldToSort, sortDirection))
            : category.items,
        })),
      }));

      return {
        ...prev,
        stockCheck: {
          ...prev.stockCheck,
          tempCountZoneItems: updatedData,
        },
      };
    });
  }, []);

  const togglePartialStockCountItem = useCallback((zoneIndex: number, categoryIndex: number, itemNumber: string | undefined) => {
    setGroupedStockCount((prev) => {
      if (!prev?.stockCheck?.tempCountZoneItems) return prev;

      const updatedData = prev.stockCheck.tempCountZoneItems.map((zone: TempCountZoneItem) => {
        return {
          ...zone,
          categories: zone.categories.map((category: TempCategoryItem) => {
            return {
              ...category,
              items: category.items.map((item: TempStockCheckItem) => {
                return {
                  ...item,
                  isExpanded: item.itemNumber === itemNumber ? !item.isExpanded : item.isExpanded,
                };
              }),
            };
          }),
        };
      });

      return {
        ...prev,
        stockCheck: {
          ...prev.stockCheck,
          tempCountZoneItems: updatedData,
        },
      };
    });
  }, []);

  const updateGroupedStockCount = (updatedData: TempCountZoneItem[]): void => {
    setGroupedStockCount((prevGroupedStockCount: GroupedStockCheckDetails | undefined) => ({
      ...prevGroupedStockCount,
      stockCheck: {
        ...prevGroupedStockCount?.stockCheck,
        tempCountZoneItems: updatedData,
      },
    }));
  };

  const sortPartialStockCountAllItems = useCallback((zoneIndex: number, categoryIndex: number, fieldToSort: keyof TempStockCheckItem, sortDirection: 'asc' | 'desc') => {
    setGroupedStockCount((prev) => {
      if (!prev?.stockCheck?.tempCountZoneAllItems) return prev;

      const updatedData = prev.stockCheck.tempCountZoneAllItems.map((zone, zIndex) => ({
        ...zone,
        categories: zone.categories.map((category, cIndex) => ({
          ...category,
          items: zIndex === zoneIndex && cIndex === categoryIndex
            ? [...category.items].sort((a, b) => compare(a, b, fieldToSort, sortDirection))
            : category.items,
        })),
      }));

      return {
        ...prev,
        stockCheck: {
          ...prev.stockCheck,
          tempCountZoneAllItems: updatedData,
        },
      };
    });
  }, []);

  const togglePartialStockCountAllItems = useCallback((zoneIndex: number, categoryIndex: number, itemNumber: string | undefined) => {
    setGroupedStockCount((prev) => {
      if (!prev?.stockCheck?.tempCountZoneAllItems) return prev;

      const updatedData = prev.stockCheck.tempCountZoneAllItems.map((zone: TempCountZoneItem) => {
        return {
          ...zone,
          categories: zone.categories.map((category: TempCategoryItem) => {
            return {
              ...category,
              items: category.items.map((item: TempStockCheckItem) => {
                return {
                  ...item,
                  isExpanded: item.itemNumber === itemNumber ? !item.isExpanded : item.isExpanded,
                };
              }),
            };
          }),
        };
      });

      return {
        ...prev,
        stockCheck: {
          ...prev.stockCheck,
          tempCountZoneAllItems: updatedData,
        },
      };
    });
  }, []);

  const updateGroupedFullStockCount = (updatedData: TempCountZoneItem[]): void => {
    setGroupedStockCount((prevGroupedStockCount: GroupedStockCheckDetails | undefined) => ({
      ...prevGroupedStockCount,
      stockCheck: {
        ...prevGroupedStockCount?.stockCheck,
        tempCountZoneAllItems: updatedData,
      },
    }));
  };

  const getItemTotalsByType = (stockCheck: StockCheck): Record<string, number> => {
    const items = stockCheck.items ?? [];

    const totals = items.reduce((acc, item) => {
      const itemType = item.productType && item.productType.trim() ? item.productType : 'No Type';
      acc[itemType] = (acc[itemType] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.fromEntries(
      Object.entries(totals).sort(([, a], [, b]) => b - a),
    );
  };

  const goBack = useCallback(() => {
    navigate('/store/stock-count');
  }, [navigate]);

  const shouldDisplayApproveBtn = () => {
    if (isCompleted) return false;

    if (hasPermissionTo(['StockCountAdmin'])) return true;

    if (!hasPermissionTo(['StockCountApprove'])) return false;

    const stockCheck = groupedStockCount?.stockCheck;

    // Check if stock count was created with an approver configured
    const hasApproverConfigured = Boolean(
      (stockCheck?.approver && stockCheck.approver.trim() !== '') ||
      stockCheck?.approverRoleId,
    );

    if (!hasApproverConfigured) {
      return true;
    }

    if (stockCheck?.approverType === ApproverType.Role) {
      return user?.persona?.id === stockCheck?.approverRoleId;
    }

    // Individual approver - check email match
    const userEmail = user?.emailAddress?.toLowerCase();
    const approverEmail = stockCheck?.approver?.toLowerCase();
    return Boolean(userEmail && approverEmail && userEmail === approverEmail);
  };

  return (
    <PageArea>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, alignContent: 'center', p: '24px' }}>
        <Box
          display='flex'
          flexDirection='row'
          justifySelf='start'
          alignItems='center'
          sx={{ color: theme.palette.primary.main }}
        >
          <IconButton aria-label={t('goBack')}
            sx={{ color: theme.palette.primary.main }}
            size='small'
            onClick={goBack}
          >
            <ArrowLeft />
          </IconButton>
          <Typography variant='textSM'>{t('goBack')}</Typography>
        </Box>
        <Box
          sx={{
            textAlign: 'left',
            color: 'black',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            alignSelf: 'stretch',
            width: '100%',
          }}>
          {loading ? (
            <Skeleton variant="rounded"
              height={40}
              width={300}
              animation="wave"
              sx={{ my: 3 }}
            />
          ) : (
            <PageTitle>{t('stockCount')} {groupedStockCount?.stockCheck?.name ?? ''}</PageTitle>
          )
          }
          {groupedStockCount?.stockCheck?.status ? (
            <Typography variant="textSM">
              {t('status')} {StockCheckStatusMap[groupedStockCount?.stockCheck?.status as keyof typeof StockCheckStatusMap]}
            </Typography>
          ) : (
            <Skeleton variant="rounded"
              height={30}
              width={150}
              animation="wave"
              sx={{ my: 3 }}
            />
          )
          }
          <Typography variant="textSM"
            display="block">
            {t('stockCountType')}: {groupedStockCount?.stockCheck?.stockCheckType != null &&
              StockCheckType[groupedStockCount?.stockCheck?.stockCheckType as StockCheckType]
              ? StockCheckTypeLabels[groupedStockCount?.stockCheck?.stockCheckType as StockCheckType]
              : t('noType')}
          </Typography>
        </Box>
        {shouldDisplayApproveBtn() && (
          <Box
            display="flex"
            justifyContent="flex-end"
            my={4}
          >
            <Button
              variant="primary"
              onClick={() => onShowConfirmApprovalModal()}
              disabled={isApproving}
            >
              {t('approve')}
            </Button>
          </Box>
        )}
        <Box
          sx={{ p: '12px', border: `solid 1px ${theme.palette.custom.gray[200]}`, borderRadius: '4px' }}
        >
          {
            loading ? (
              <Box display="flex"
                flexDirection="row"
                justifyContent='space-between'>
                <Skeleton animation="wave"
                  variant="rounded"
                  width="33%"
                  height={50} />
                <Skeleton animation="wave"
                  variant="rounded"
                  width="33%"
                  height={50} />
                <Skeleton animation="wave"
                  variant="rounded"
                  width="33%"
                  height={50} />
              </Box>
            ) : (
              <StockCheckHeader
                stockCheckId={stockCheckId}
                countedBy={groupedStockCount?.stockCheck?.countedBy ?? ''}
                approvedBy={groupedStockCount?.stockCheck?.approvedBy ?? ''}
                plannedCountDate={new Date(groupedStockCount?.stockCheck?.plannedCountDate ?? '').toDateString()}
                isCompleted={isCompleted}
                hasPosData={groupedStockCount?.hasPosData}
                lastPosDate={groupedStockCount?.lastPosDate}
                status={groupedStockCount?.stockCheck?.status}
              />
            )
          }
          {!isCompleted && <Box
            display="flex"
            flex={1}
            flexDirection="row"
            justifyContent="space-between"
            pt={8}
            alignItems="center"
          >
            {
              saveErrorMessage &&
              <Alert severity="error"
                sx={{ display: 'flex', flex: 3, mr: 7 }}>{saveErrorMessage}</Alert>
            }
            <Box
              display="flex"
              flex={1}
              flexDirection='row'
              justifyContent="flex-end"
              alignItems="center"
            >
              <Button
                variant="bad"
                onClick={rejectAllApprovalItems}
                disabled={isApproving || isBulkUpdating}
              >
                {t('rejectAll')}
              </Button>
              <Button
                variant="primary"
                onClick={approveAllApprovalItems}
                disabled={isApproving || isBulkUpdating}
                sx={{ marginLeft: 3 }}
              >
                {t('acceptAll')}
              </Button>
            </Box>
          </Box>
          }
          {
            loading || isBulkUpdating ? (
              <Box display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='center'
                py={20}>
                <CircularProgress size={60} />
              </Box>
            ) : (
              <StockCheckContent
                groupedStockCountItems={groupedStockCount?.stockCheck?.tempCountZoneItems}
                onItemStatusChange={handleItemStatusChange}
                sortPartialStockCountItems={sortPartialStockCountItems}
                togglePartialStockCountItem={togglePartialStockCountItem}
                isDisabled={isCompleted || isApproving}
                currencyCode={currencyCode ?? ''}
              />
            )
          }
          {
            loading ? (
              <Box display='flex'
                flexDirection='column'
                rowGap={5}
                my={10}>
                <Skeleton animation="wave"
                  variant="rounded"
                  width="100%"
                  height={30} />
                <Skeleton animation="wave"
                  variant="rounded"
                  width="100%"
                  height={30} />
                <Skeleton animation="wave"
                  variant="rounded"
                  width="100%"
                  height={30} />
              </Box>
            ) : (
              <>
                {
                  !isCompleted && <>
                    <Box sx={{ background: theme.palette.custom.gray[200], height: 3, my: 5 }} />
                    <Box
                      display='flex'
                      flexDirection='row'
                      justifyContent='space-between'
                      alignItems='center'
                      py={3}
                    >
                      <Typography
                        variant='textMD'
                        sx={{ display: 'flex', justifySelf: 'start' }}
                      >
                        {t('viewFull')}
                      </Typography>
                      <Box display='flex'
                        flexDirection='row'
                        justifySelf='end'
                        alignItems='center'
                      >
                        <Typography variant='textMD'>{t('view')}</Typography>
                        <IconButton onClick={() => setIsViewMode(!isViewMode)}>
                          {isViewMode ? <ChevronUp /> : <ChevronDown />}
                        </IconButton>
                      </Box>
                    </Box>
                    <Collapse in={isViewMode}>
                      <StockCheckContent
                        groupedStockCountItems={groupedStockCount?.stockCheck?.tempCountZoneAllItems}
                        sortPartialStockCountItems={sortPartialStockCountAllItems}
                        togglePartialStockCountItem={togglePartialStockCountAllItems}
                        isDisabled={true}
                        isViewMode
                        currencyCode={currencyCode ?? ''}
                      />
                    </Collapse>
                  </>
                }
                <Box sx={{ background: theme.palette.custom.gray[200], height: 3, my: 5 }} />
                <Box
                  display='flex'
                  flexDirection='row'
                  justifyContent='space-between'
                  alignItems='center'
                  py={3}
                >
                  <Typography
                    variant='textMD'
                    sx={{ display: 'flex', justifySelf: 'start' }}
                  >
                    {t('itemSummaryByType')}
                  </Typography>
                  <Box display='flex'
                    flexDirection='row'
                    justifySelf='end'
                    alignItems='center'
                  >
                    <Typography variant='textMD'>{t('view')}</Typography>
                    <IconButton onClick={() => setIsTypeViewMode(!isTypeViewMode)}>
                      {isTypeViewMode ? <ChevronUp /> : <ChevronDown />}
                    </IconButton>
                  </Box>
                </Box>
                <Collapse in={isTypeViewMode}>
                  <Grid container
                    spacing={2}>
                    {Object.entries(getItemTotalsByType(groupedStockCount?.stockCheck || {})).map(([type, count]) => (
                      <Grid item
                        xs={3}
                        key={type}>
                        <Box display="flex"
                          alignItems="center"
                          gap={1}>
                          <Typography>
                            {t(type)}
                          </Typography>
                          <Pill text={`${count} items`} />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Collapse>
              </>
            )
          }

        </Box>
        <StockCheckApproveDialog
          open={showApproveDialog}
          onClose={() => setShowApproveDialog(false)}
          onSubmit={submitApprovedStockCount}
          onItemStatusChange={handleItemStatusChange}
          isApproving={isApproving}
          maxToleranceWarningValue={groupedStockCount?.maxToleranceWarningValue}
          itemsAboveMaxTolerance={itemsAboveMaxTolerance}
          setItemsAboveMaxTolerance={setItemsAboveMaxTolerance}
          currencyCode={currencyCode ?? ''}
        />
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          open={showSubmitStatusMessage}
          onClose={() => setShowSubmitStatusMessage(false)}
          autoHideDuration={5000}
        >
          <Alert
            onClose={() => setShowSubmitStatusMessage(false)}
            severity={isSubmittedSuccessfully ? 'success' : 'error'}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {isSubmittedSuccessfully ? t('successMessage') : t('errorMessage') + ' ' + errorMessage}
          </Alert>
        </Snackbar>
      </Box>
    </PageArea>
  );
};

export default StockCountReviewPage;
